import { z } from "zod";
import {
  createGetListRequestSchema,
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
  ResourcesSchema,
} from "../util/index.ts";
import { LookupWithDetailsValueSchema } from "./AttributeSchemas.ts";
import {
  FormSchemaMeasurementQuestionSchema,
  FormSchemaQuestionAnswerSchema,
  FormSchemaSchema,
} from "./FormSchemaSchemas.ts";
import { FormSubmissionStatus } from "./FormSubmissionSchemas.shared.ts";

export const FormSubmissionNormalQuestionAnswerSchema = z.union([
  z.object({
    value: FormSchemaQuestionAnswerSchema,
  }),
  LookupWithDetailsValueSchema,
]);
export type FormSubmissionLookupDetailsQuestionAnswerType = z.infer<
  typeof LookupWithDetailsValueSchema
>;
export type FormSubmissionNormalQuestionAnswerType = z.infer<
  typeof FormSubmissionNormalQuestionAnswerSchema
>;

export const FormSubmissionMeasurementQuestionAnswerSchema = z
  .object({
    source_id: z.string().nullable().optional(),
    subject_id: z.string().nullable().optional(),
    subject_type: ResourcesSchema.nullable().optional(),
    time: z.string(),
    value: FormSchemaQuestionAnswerSchema,
  })
  .merge(
    FormSchemaMeasurementQuestionSchema.pick({
      measurement_type: true,
      measurement_unit: true,
    }).partial()
  );
export type FormSubmissionMeasurementQuestionAnswerType = z.infer<
  typeof FormSubmissionMeasurementQuestionAnswerSchema
>;

export const FormSubmissionAnswerSchema = z.union([
  FormSubmissionNormalQuestionAnswerSchema,
  FormSubmissionMeasurementQuestionAnswerSchema,
]);
export type FormSubmissionAnswerType = z.infer<
  typeof FormSubmissionAnswerSchema
>;

export const FormSubmissionAnswersByQuestionMapSchema = z.record(
  FormSubmissionAnswerSchema
);
export type FormSubmissionAnswersByQuestionMapType = z.infer<
  typeof FormSubmissionAnswersByQuestionMapSchema
>;

export const FormSubmissionAnswersBySectionMapSchema = z.record(
  z.array(FormSubmissionAnswersByQuestionMapSchema)
);
export type FormSubmissionAnswersBySectionMapType = z.infer<
  typeof FormSubmissionAnswersBySectionMapSchema
>;

export const FormSubmissionSchema = z
  .object({
    company_id: z.string().uuid(),
    form_schema_id: z.string().uuid(),
    form_schema_version: z.number().int(),
    form_schema: FormSchemaSchema.optional(),
    status: z.nativeEnum(FormSubmissionStatus),
    retrigger_workflow: z.boolean().optional(),
    version: z.number(),
    answers: FormSubmissionAnswersBySectionMapSchema,
    dataset_submission_id: z.string().optional(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema)
  .describe("Represents a Form Submission in the Node API.");
export type FormSubmissionType = z.infer<typeof FormSubmissionSchema>;

export const FormSubmissionGetListRequestSchema = createGetListRequestSchema(
  z
    .object({
      name: z.string(),
      created_at: z.string(),
      created_by: z.string().uuid(),
      status: z.nativeEnum(FormSubmissionStatus),
      "form_category.id": z.string().uuid(),
      "form_category.name": z.string(),
      "form_schema.id": z.string().uuid(),
      form_schema_id: z.string().uuid(),
      related_entity_id: z.string().uuid(),
      related_entity_type: ResourcesSchema,
      dataset_submission_id: z.string().optional(),
    })
    .partial(),
  z.object({
    answers: z.boolean().optional(),
    omitSchema: z.boolean().optional(),
  })
);
export type FormSubmissionGetListRequestType = z.infer<
  typeof FormSubmissionGetListRequestSchema
>;

export const FormSubmissionWithAnswersSchema = z
  .object({
    company_id: z.string().uuid(),
    form_schema_id: z.string().uuid(),
    form_schema_version: z.number().int(),
    dataset_submission_id: z.string().optional(),
    status: z.nativeEnum(FormSubmissionStatus),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema.pick({ created_by: true }))
  .describe(
    "Represents a Form Submission with answers for a particular Form Schema version in the Node API."
  );
export type FormSubmissionWithAnswersType = z.infer<
  typeof FormSubmissionWithAnswersSchema
> &
  Record<`${string}:${string}`, string | number | boolean>;

export const FormSubmissionAnswerInFormSchema = z.object({
  value: FormSchemaQuestionAnswerSchema,
});
export type FormSubmissionAnswerInFormType = z.infer<
  typeof FormSubmissionAnswerInFormSchema
>;

export const FormSubmissionAnswersInFormByQuestionMapSchema = z.record(
  FormSubmissionAnswerInFormSchema
);
export type FormSubmissionAnswersInFormByQuestionMapType = z.infer<
  typeof FormSubmissionAnswersInFormByQuestionMapSchema
>;

export const FormSubmissionAnswersInFormBySectionMapSchema = z.record(
  z.array(FormSubmissionAnswersInFormByQuestionMapSchema)
);
export type FormSubmissionAnswersInFormBySectionMapType = z.infer<
  typeof FormSubmissionAnswersInFormBySectionMapSchema
>;

export const FormSubmissionAnswersInFormSchema =
  FormSubmissionAnswersInFormBySectionMapSchema;
export type FormSubmissionAnswersInFormType = z.infer<
  typeof FormSubmissionAnswersInFormSchema
>;
